import { type FC, useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { X } from "lucide-react";

import { Dialog, DialogContent, DialogTitle, DialogClose } from "@/shared/components/ui/dialog";
import { supabase } from "@/shared/lib/supabase";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useLawyerClientInvitations } from "@/shared/hooks/useLawyerClientInvitations";
import { LAWYER_ROUTES } from "@/shared/constants/paths";
import { EventType, trackEvent } from "@/shared/utils/core";

import type { LawyerPlanKey } from "@/shared/constants/lawyerPlans";

import { WizardStepper } from "./WizardStepper";
import { ClientInfoStep } from "./ClientInfoStep";
import { ChoosePlanStep } from "./ChoosePlanStep";
import { ReviewStep } from "./ReviewStep";
import { ConfirmationStep } from "./ConfirmationStep";
import { IconButton } from "@/shared/components/ui/icon-button";

interface InviteClientWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ClientInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

const INITIAL_CLIENT_INFO: ClientInfo = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
};

type WizardStep = 1 | 2 | 3 | 4;

const STEP_NAMES: Record<WizardStep, string> = {
  1: "client_info",
  2: "choose_plan",
  3: "review",
  4: "confirmation",
};

export const InviteClientWizard: FC<InviteClientWizardProps> = ({ isOpen, onClose }) => {
  const { user } = useAppContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { sendClientInvitation, isSending } = useLawyerClientInvitations();

  const [step, setStep] = useState<WizardStep>(1);
  const [clientInfo, setClientInfo] = useState<ClientInfo>(INITIAL_CLIENT_INFO);
  const [selectedPlan, setSelectedPlan] = useState<LawyerPlanKey | null>("estate_setup");
  const [inviteLink, setInviteLink] = useState("");
  const [offerApplied, setOfferApplied] = useState(false);
  const [createdEstateId, setCreatedEstateId] = useState<number | null>(null);
  const sendingRef = useRef(false);

  const { data: lawyerData } = useQuery({
    queryKey: ["lawyer-invite-data", user?.user_id],
    queryFn: async () => {
      if (!user?.user_id) return null;
      const { data } = await supabase
        .from("lawyers")
        .select("first_client_used, firm_name")
        .eq("user_id", user.user_id)
        .maybeSingle();
      return data;
    },
    enabled: !!user?.user_id,
    staleTime: 2 * 60 * 1000,
  });

  const firstClientOfferAvailable = lawyerData?.first_client_used === false;
  const firmName = lawyerData?.firm_name ?? "";

  useEffect(() => {
    if (isOpen) {
      trackEvent(EventType.ATTORNEY_INVITE_WIZARD_STEP_VIEWED, {
        step_number: step,
        step_name: STEP_NAMES[step],
      });
    }
  }, [isOpen, step]);

  const advanceTo = useCallback(
    (nextStep: WizardStep) => {
      trackEvent(EventType.ATTORNEY_INVITE_WIZARD_STEP_COMPLETED, {
        step_number: step,
        step_name: STEP_NAMES[step],
      });
      setStep(nextStep);
    },
    [step],
  );

  const handleReset = useCallback(() => {
    setStep(1);
    setClientInfo(INITIAL_CLIENT_INFO);
    setSelectedPlan("estate_setup");
    setInviteLink("");
    setOfferApplied(false);
    setCreatedEstateId(null);
  }, []);

  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [handleReset, onClose]);

  const handleSend = useCallback(async () => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    try {
      const result = await sendClientInvitation.mutateAsync({
        clientEmail: clientInfo.email,
        clientFirstName: clientInfo.firstName,
        clientLastName: clientInfo.lastName,
        clientPhone: clientInfo.phone || undefined,
        recommendedPlan: selectedPlan,
        firstClientOfferApplied: firstClientOfferAvailable,
        firmName,
      });

      setInviteLink(`${window.location.origin}/client-invite?token=${result.invitation_token}`);
      setOfferApplied(result.first_client_offer_applied ?? false);
      setCreatedEstateId(result.estate_id);
      queryClient.invalidateQueries({ queryKey: ["lawyer-persons"] });
      queryClient.invalidateQueries({ queryKey: ["lawyer-invite-data"] });
      queryClient.invalidateQueries({ queryKey: ["lawyer-estate-nav"] });
      advanceTo(4);
    } finally {
      sendingRef.current = false;
    }
  }, [
    clientInfo,
    selectedPlan,
    firstClientOfferAvailable,
    sendClientInvitation,
    queryClient,
    firmName,
    advanceTo,
  ]);

  const handleStartRecord = useCallback(() => {
    handleClose();
    if (createdEstateId) {
      navigate(LAWYER_ROUTES.CLIENT_DETAIL.replace(":userId", String(createdEstateId)), {
        state: { tab: "documents", openUpload: true },
      });
    }
  }, [handleClose, navigate, createdEstateId]);

  const widthClass =
    step === 3 ? "sm:max-w-[896px]" : step === 4 ? "sm:max-w-[672px]" : "sm:max-w-[784px]";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className={`gap-0 p-0 ${widthClass}`}>
        <DialogTitle className="sr-only">Invite a client</DialogTitle>
        <DialogClose asChild>
          <IconButton className="absolute right-4 top-4 z-10" label="Close" tooltip={false}>
            <X />
          </IconButton>
        </DialogClose>
        {step < 4 && (
          <div className="border-b border-primary px-6 py-4">
            <WizardStepper currentStep={step as 1 | 2 | 3} />
          </div>
        )}

        {step === 1 && (
          <ClientInfoStep
            clientInfo={clientInfo}
            onChange={setClientInfo}
            onNext={() => advanceTo(2)}
            firstClientOfferAvailable={firstClientOfferAvailable}
          />
        )}

        {step === 2 && (
          <ChoosePlanStep
            selectedPlan={selectedPlan}
            onSelect={setSelectedPlan}
            onNext={() => advanceTo(3)}
            onBack={() => setStep(1)}
            firstClientOfferAvailable={firstClientOfferAvailable}
          />
        )}

        {step === 3 && (
          <ReviewStep
            clientInfo={clientInfo}
            selectedPlan={selectedPlan}
            firstClientOfferAvailable={firstClientOfferAvailable}
            onBack={() => setStep(2)}
            onEditClient={() => setStep(1)}
            onSend={handleSend}
            isSending={isSending}
          />
        )}

        {step === 4 && (
          <ConfirmationStep
            clientFirstName={clientInfo.firstName}
            inviteLink={inviteLink}
            firstClientOfferApplied={offerApplied}
            onDone={handleClose}
            onInviteAnother={handleReset}
            onStartRecord={handleStartRecord}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
